// Decides how the label scanner reports a failed scan (#6664). Kept out of label-scanner-page.ts so
// it stays loadable under plain Node for its unit test, like services/reminder-server-failure.ts.

import { ScanLabelError, ScanLabelResults } from '@cf-shared/scan-label-interface';
import { wasReportedByCloudFunctions } from 'services/cloud-functions-errors';

/** The `label_scan_error` value for a failure the network layer owns (the equipment scanner's value). */
const NETWORK_TELEMETRY_ERROR = 'network';

/** A label scan result as the page sees it. `handledByNetworkLayer` is client-only and never sent. */
export type LabelScanOutcome = ScanLabelResults & { handledByNetworkLayer?: boolean };

/** What the page reports for a failed scan: the `label_scan_error` value and whether to file a Sentry problem. */
export interface LabelScanFailureReport {
  telemetryError: string;
  fileProblem: boolean;
}

/** The multi-model loop's result for a request that threw, or for the offline pre-check (`err` null).
 * The error stays ProviderError so the alert, kept photos and Try Again are unchanged; the flag says
 * whether the cloud-functions layer already reported the failure or deliberately left it unreported. */
export function makeLabelScanNetworkErrorResult(modelKey: string | null, err: unknown): LabelScanOutcome {
  return {
    error: ScanLabelError.ProviderError,
    modelKey: modelKey ?? undefined,
    // if nothing was sent (offline) or callable() already filed the one report, the scanner must not file another
    handledByNetworkLayer: err === null || wasReportedByCloudFunctions(err),
  };
}

/** One multi-model run's memory of its failed attempts. The loop returns only the LAST failure, so
 * without this a dropped last upload would hide an earlier attempt nobody else reported (a provider
 * error the server sent, or an unreported thrown error). Create one per run. */
export class LabelScanRunTracker {
  private sawUnreportedFailure = false;

  /** Pass as the loop's `makeNetworkErrorResult`. */
  readonly makeNetworkErrorResult = (modelKey: string | null, err: unknown): LabelScanOutcome => {
    const result = makeLabelScanNetworkErrorResult(modelKey, err);
    if (!result.handledByNetworkLayer) this.sawUnreportedFailure = true;
    return result;
  };

  /** Call from the loop's `onProviderError`: the server only logs provider failures. */
  noteProviderError(): void {
    this.sawUnreportedFailure = true;
  }

  /** The run's final result, marked handled only when every failed attempt was. */
  finish(result: LabelScanOutcome | null): LabelScanOutcome | null {
    // if an earlier attempt failed with nobody reporting it, the scanner's report is its only one
    if (result?.handledByNetworkLayer && this.sawUnreportedFailure) return { ...result, handledByNetworkLayer: false };
    return result;
  }
}

/** What to report for a failed scan. A failure the network layer handled is a network drop, not a
 * provider failure, so it is sent as `network` with no second Sentry problem. Anything else reached
 * the scanner with nobody having reported it, so it keeps its own report. */
export function labelScanFailureReport(handledByNetworkLayer: boolean, error: string): LabelScanFailureReport {
  if (handledByNetworkLayer) return { telemetryError: NETWORK_TELEMETRY_ERROR, fileProblem: false };
  return { telemetryError: error, fileProblem: true };
}
